import { CssBaseline, ThemeProvider as MuiThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { CacheProvider } from '@emotion/react';
import 'dayjs/locale/en';
import 'dayjs/locale/ar-iq';
import 'dayjs/locale/ku';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { useEffect, useMemo, type PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { createAppTheme } from '.';
import { usePreferencesStore } from '@/store/preferences';
import { isRtlLocale } from '@/i18n/locales';
import { applyDocumentLanguage } from '@/i18n/documentLanguage';

const ltrCache = createCache({ key: 'mui', stylisPlugins: [prefixer] });
const rtlCache = createCache({ key: 'mui-rtl', stylisPlugins: [prefixer, rtlPlugin] });

/**
 * dayjs locales for the pickers. `ar-iq` rather than `ar`: the plain Arabic
 * locale swaps in Arabic-Indic digits, which the picker's segmented field
 * cannot parse, and the Iraqi one keeps Latin digits with Levantine month
 * names. `ku` (Sorani) covers ckb the same way.
 */
const DAYJS_LOCALE: Record<string, string> = { en: 'en', ar: 'ar-iq', ckb: 'ku' };

/**
 * Owns four coupled concerns that must change together: colour mode, text
 * direction, the Emotion cache that flips CSS logical properties for RTL, and
 * the date adapter locale the pickers format with.
 */
export function AppThemeProvider({ children }: PropsWithChildren) {
  const { i18n } = useTranslation();
  const mode = usePreferencesStore((s) => s.resolvedMode());
  const rtl = isRtlLocale(i18n.language);
  const direction = rtl ? 'rtl' : 'ltr';

  const theme = useMemo(() => createAppTheme(mode, direction), [mode, direction]);
  const adapterLocale = DAYJS_LOCALE[i18n.language.split('-')[0]] ?? 'en';

  useEffect(() => {
    // `lang`, `dir` and the translation opt-out move together; see
    // `i18n/documentLanguage.ts` for why the opt-out is re-asserted here.
    applyDocumentLanguage(i18n.language);
    document.documentElement.style.colorScheme = mode;
  }, [i18n.language, mode]);

  return (
    <CacheProvider value={rtl ? rtlCache : ltrCache}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
          {children}
        </LocalizationProvider>
      </MuiThemeProvider>
    </CacheProvider>
  );
}
